import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Loader2, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LeadModal — dual-purpose lead capture.
 *
 * mode="crm-lead"        → POST /api/public/webhooks/crm-lead
 * mode="partner-quote"   → POST /api/public/webhooks/partner-fulfillment
 *
 * context props (per-mode extras baked into payload):
 *   crm-lead      : { plan_id, plan_name }
 *   partner-quote : { hw_config_id, hw_config_label, hw_price_hint }
 */
export default function LeadModal({ open, onClose, mode, title, subtitle, context = {} }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [country, setCountry] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState(null);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (open) {
      setState("idle");
      setError(null);
      setTimeout(() => firstFieldRef.current?.focus(), 40);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;

  const isQuote = mode === "partner-quote";
  const endpoint = isQuote
    ? `${BACKEND_URL}/api/public/webhooks/partner-fulfillment`
    : `${BACKEND_URL}/api/public/webhooks/crm-lead`;

  async function submit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setState("sending");
    setError(null);
    try {
      const body = isQuote
        ? {
            name, email, company, phone, message,
            hw_config_id: context.hw_config_id,
            hw_config_label: context.hw_config_label,
            hw_price_hint: context.hw_price_hint,
            quantity: Math.max(1, parseInt(quantity, 10) || 1),
            shipping_country: country || null,
          }
        : {
            name, email, company, phone, message,
            plan_id: context.plan_id,
            plan_name: context.plan_name,
            source: context.source || "pricing",
          };
      await axios.post(endpoint, body);
      setState("done");
    } catch (err) {
      setState("error");
      setError(err?.response?.data?.detail?.[0]?.msg || err?.message || "Submit failed");
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="lead-modal-backdrop"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-sm border border-zinc-800 bg-[#0b0b0e] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-modal"
      >
        <div className="flex items-start justify-between p-5 border-b border-zinc-800">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">
              {isQuote ? "§ Hardware quote" : "§ Talk to sales"}
            </div>
            <div className="font-display font-bold text-lg mt-1">{title}</div>
            {subtitle && <div className="text-xs text-zinc-500 mt-1">{subtitle}</div>}
          </div>
          <button
            data-testid="lead-modal-close"
            aria-label="Close"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {state === "done" ? (
          <div className="p-6 text-center" data-testid="lead-modal-success">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400" />
            <div className="font-display font-bold text-lg mt-3">Thanks, we&apos;re on it.</div>
            <div className="text-sm text-zinc-400 mt-1">
              A MOXYPLAY {isQuote ? "hardware partner" : "sales engineer"} will reply to{" "}
              <span className="text-zinc-200">{email}</span> within one business day.
            </div>
            <button
              onClick={onClose}
              data-testid="lead-modal-done-btn"
              className="mt-5 tenant-btn font-mono text-[11px] uppercase tracking-widest rounded-sm px-4 py-2"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Full name" required>
                <input
                  ref={firstFieldRef}
                  data-testid="lead-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-zinc-900/60 border border-zinc-800 focus:border-emerald-500/60 rounded-sm px-2 py-1.5 text-sm font-mono outline-none transition-colors"
                  placeholder="Alex Rivera"
                  required
                />
              </Field>
              <Field label="Work email" required>
                <input
                  data-testid="lead-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-zinc-900/60 border border-zinc-800 focus:border-emerald-500/60 rounded-sm px-2 py-1.5 text-sm font-mono outline-none transition-colors"
                  placeholder="alex@studio.tv"
                  required
                />
              </Field>
              <Field label="Company">
                <input
                  data-testid="lead-company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full bg-zinc-900/60 border border-zinc-800 focus:border-emerald-500/60 rounded-sm px-2 py-1.5 text-sm font-mono outline-none transition-colors"
                  placeholder="Studio Inc."
                />
              </Field>
              <Field label="Phone">
                <input
                  data-testid="lead-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-zinc-900/60 border border-zinc-800 focus:border-emerald-500/60 rounded-sm px-2 py-1.5 text-sm font-mono outline-none transition-colors"
                  placeholder="+1 555 0100"
                />
              </Field>
              {isQuote && (
                <>
                  <Field label="Quantity">
                    <input
                      data-testid="lead-quantity"
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="w-full bg-zinc-900/60 border border-zinc-800 focus:border-emerald-500/60 rounded-sm px-2 py-1.5 text-sm font-mono outline-none transition-colors"
                    />
                  </Field>
                  <Field label="Shipping country">
                    <input
                      data-testid="lead-country"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="w-full bg-zinc-900/60 border border-zinc-800 focus:border-emerald-500/60 rounded-sm px-2 py-1.5 text-sm font-mono outline-none transition-colors"
                      placeholder="US, IN, DE…"
                    />
                  </Field>
                </>
              )}
            </div>
            <Field label={isQuote ? "Deployment notes" : "What are you trying to ship?"}>
              <textarea
                data-testid="lead-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full bg-zinc-900/60 border border-zinc-800 focus:border-emerald-500/60 rounded-sm px-2 py-1.5 text-sm font-mono outline-none transition-colors min-h-[70px]"
                placeholder={isQuote
                  ? "e.g. FRC 25p → 29.97p for OTT, dual-PSU, 4× SDI in / 8× SDI out"
                  : "e.g. 5-channel FAST bouquet, HLS + XMLTV, launching in 4 weeks"}
                rows={3}
              />
            </Field>
            {error && (
              <div className="text-[11px] font-mono text-red-400" data-testid="lead-error">{error}</div>
            )}
            <div className="flex items-center justify-between pt-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                {isQuote
                  ? `HW · ${context.hw_config_label || context.hw_config_id || "config"}`
                  : `Plan · ${context.plan_name || context.plan_id || "any"}`}
              </div>
              <button
                type="submit"
                disabled={state === "sending"}
                data-testid="lead-submit"
                className="tenant-btn font-mono text-[11px] uppercase tracking-widest rounded-sm px-4 py-2 disabled:opacity-60 flex items-center gap-2"
              >
                {state === "sending" && <Loader2 className="w-3 h-3 animate-spin" />}
                {isQuote ? "Request quote" : "Contact sales"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}
